import { NodeSource, TreeAdapter, TreeAdapterParams, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp from "@reporting/rp-app-host";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { PeriodItem } from "@reporting/rp-types/analytics";
import { PropertiesDefinition } from "@reporting/rp-types/definition";

export class DimTreeAdapter extends TreeAdapter {

	private branches = new Map<string, NodeSource[]>();
	public accounting: number;

	constructor(
		private module: ModuleCode,
		private dimNum: string,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(params: TreeAdapterParams): Promise<TreeSource> {
		if (!this.branches.has(params.parentKey)) {
			try {
				const data = await rp.ms('analytics').get<TreeSource>(`dimensionTree/${this.module}/${this.dimNum}`, {
					parent: params.parentKey,
					accounting: this.accounting
				});
				data.forEach(node => node.collapsed = true);
				this.branches.set(params.parentKey, data);
			} catch (error) {
				return [
					{k: null, v: error, icon: {icon: "hexagon-exclamation", color: 'red'}}
				];
			}
		}

		return this.branches.get(params.parentKey);
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

/**
 * Values of all attributes of one dimension, grouped by attribute. The node key is the
 * `{"<attribute key>": "<attribute value>"}` pair the way it is stored in the database.
 */
export class DimAttributeValueTreeAdapter extends TreeAdapter {

	private items: TreeSource;

	constructor(
		private module: ModuleCode,
		private dimNum: string,
		private properties: PropertiesDefinition[],
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		this.items = [];
		for (const property of this.properties) {
			const values = await rp.ms('analytics')
				.get<{v: string}[]>(`dimensionAttributes/${this.module}/${this.dimNum}/${encodeURIComponent(property.key)}`)
				.then(values => values.filter(item => item.v != null && item.v !== ''));
			if (!values.length) continue;
			this.items.push({k: null, v: property.name || property.key, l: 0, ch: false});
			values.forEach(item => this.items.push({
				k: JSON.stringify({[property.key]: item.v}),
				v: item.v,
				l: 1
			}));
		}
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

export class PeriodAdapter extends TreeAdapter {

	private items: NodeSource[];
	public accounting: number;

	constructor(
		private module: ModuleCode,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(params: TreeAdapterParams): Promise<TreeSource> {
		if (this.items != null) return this.items;

		const data = await rp.ms('analytics').get<PeriodItem[]>(`periodTree`, {
			m: this.module,
			accounting: this.accounting
		});
		this.items = data.map(item => ({
			k: item.id_period,
			v: item.period,
			l: item.level,
			collapsed: true
		}));

		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {
			selectMode: 'multiple'
		};
	}

}
